import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, Loader2, Save, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';
import { usePermission } from '../rbac/use-permission';
import { P } from '../../lib/rbac/permissions.generated';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { SlotEditor } from './SlotEditor';
import { CmsBlockEditor } from './CmsBlockEditor';
import {
  buildPreviewUrl,
  cmsApi,
  WEBSITE_BASE_URL,
  type CmsBlockSchema,
  type CmsContent,
  type CmsContentSchema,
  type CmsContentValue,
} from '../../lib/api/cms';

type Block = Record<string, unknown> & { type: string };

interface CmsContentEditorProps {
  slug: string;
  /** Optional heading override; defaults to the content's title. */
  title?: string;
  description?: string;
  /**
   * Explicit website path for the preview link (e.g. "/fach/klavier").
   * Required for slug-routed entity pages where the CMS slug ("fach/3") does
   * not match the public URL. Falls back to a slug-derived path when omitted.
   */
  previewPath?: string;
  /**
   * Restrict the editor to a subset of the schema's fields (by key). Other
   * fields are preserved untouched in the saved document. Use for focused
   * editors (e.g. a Standort-Admin editing only the pinboard).
   */
  onlyKeys?: string[];
  /** Hide specific fields (by key) while preserving them in the saved document. */
  excludeKeys?: string[];
  /** Hide the preview button (e.g. when the public URL can't be resolved). */
  hidePreview?: boolean;
}

const stable = (v: unknown): string => JSON.stringify(v ?? {});

/**
 * Extract entity ids from parameterised slugs for the entity-aware widgets:
 * "standort/{id}" → { standortId }, "standort-fach/{sid}-{fid}" → both.
 */
function parseEditorContext(slug: string): { standortId?: number; fachId?: number } {
  const trimmed = slug.trim();
  const standort = /^standort\/(\d+)$/.exec(trimmed);
  if (standort) return { standortId: Number(standort[1]) };
  const combo = /^standort-fach\/(\d+)-(\d+)$/.exec(trimmed);
  if (combo) return { standortId: Number(combo[1]), fachId: Number(combo[2]) };
  return {};
}

const asTrimmed = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const stripHtml = (html: string): string => html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * Build the inheritance hints for the Standort×Fach editor from the universal
 * Fach page's content (field key -> display string). Prefers published content
 * (what the website actually shows), falling back to the draft.
 */
function buildInheritedFromFach(fach: CmsContent | null): Record<string, string> {
  if (!fach) return {};
  const src =
    fach.published && Object.keys(fach.published).length ? fach.published : fach.draft || {};
  const out: Record<string, string> = {};
  const headline = asTrimmed(src.heroHeadline);
  if (headline) out.heroHeadline = headline;
  const tagline = asTrimmed(src.heroTagline);
  if (tagline) out.heroTagline = tagline;
  const beschreibung = stripHtml(asTrimmed(src.beschreibung));
  if (beschreibung) out.beschreibung = beschreibung;
  const benefits = Array.isArray(src.benefits)
    ? src.benefits.map((b) => asTrimmed((b as { text?: unknown })?.text)).filter(Boolean).join(' · ')
    : '';
  if (benefits) out.benefits = benefits;
  return out;
}

export const CmsContentEditor: React.FC<CmsContentEditorProps> = ({ slug, title, description, previewPath, onlyKeys, excludeKeys, hidePreview }) => {
  // Knöpfe nur mit Recht zeigen — statt erst beim Klick abzulehnen. Die Navigation
  // verlangt zusätzlich ihr eigenes Recht (T-0211); der Server prüft dasselbe.
  const istNavigation = slug.replace(/^\/+|\/+$/g, '') === 'navbar';
  const darfNavigation = usePermission(P.cms.website_struktur.navigation_bearbeiten);
  const darfSpeichern = usePermission(P.cms.inhalte.inhalte_bearbeiten) && (!istNavigation || darfNavigation);
  const darfVeroeffentlichen =
    usePermission(P.cms.inhalte.inhalte_veroeffentlichen) && (!istNavigation || darfNavigation);
  const [schemas, setSchemas] = useState<Record<string, CmsContentSchema>>({});
  const [blockSchemas, setBlockSchemas] = useState<Record<string, CmsBlockSchema>>({});
  const [content, setContent] = useState<CmsContent | null>(null);
  const [draft, setDraft] = useState<CmsContentValue>({});
  const [inherited, setInherited] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fachId = parseEditorContext(slug).fachId;
      const [schemaMap, blockMap, item, fachItem] = await Promise.all([
        cmsApi.getSchemas(),
        cmsApi.getBlockSchemas(),
        cmsApi.getContent(slug),
        // Standort×Fach erbt von der Fach-Seite: deren Inhalte als Platzhalter laden.
        fachId ? cmsApi.getContent(`fach/${fachId}`).catch(() => null) : Promise.resolve(null),
      ]);
      setSchemas(schemaMap);
      setBlockSchemas(blockMap);
      setContent(item);
      setDraft(item.draft || {});
      setInherited(buildInheritedFromFach(fachItem));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Inhalt konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { void load(); }, [load]);

  const isPage = content?.kind === 'page';
  const schema = content ? schemas[content.kind] : undefined;
  const visibleFields = useMemo(() => {
    if (!schema) return [];
    let fields = schema.fields;
    if (onlyKeys) fields = fields.filter((f) => onlyKeys.includes(f.key));
    if (excludeKeys) fields = fields.filter((f) => !excludeKeys.includes(f.key));
    return fields;
  }, [schema, onlyKeys, excludeKeys]);
  const dirty = useMemo(
    () => !!content && stable(draft) !== stable(content.draft),
    [draft, content],
  );

  const persistDraft = async (): Promise<CmsContent | null> => {
    const saved = await cmsApi.saveDraft(slug, { content: draft });
    setContent(saved);
    return saved;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await persistDraft();
      toast.success('Entwurf gespeichert');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      if (dirty) await persistDraft();
      const published = await cmsApi.publish(slug);
      setContent(published);
      toast.success('Veröffentlicht');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Veröffentlichen fehlgeschlagen');
    } finally {
      setPublishing(false);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      if (dirty) await persistDraft();
      const { token } = await cmsApi.getPreviewToken();
      const url = previewPath
        ? `${WEBSITE_BASE_URL}${previewPath}?preview=${encodeURIComponent(token)}`
        : buildPreviewUrl(slug, token);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Vorschau fehlgeschlagen');
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!content || (!isPage && !schema)) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Für diesen Inhalt ist kein Schema hinterlegt ({content?.kind ?? slug}).
        </CardContent>
      </Card>
    );
  }

  const isPublished = content.status === 'published';

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-10 -mx-2 flex flex-wrap items-center justify-between gap-3 border-b bg-background/95 px-2 py-3 backdrop-blur">
        <div>
          <h1 className="text-xl font-semibold">{title ?? content.title}</h1>
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
          <div className="mt-1 flex items-center gap-2">
            <Badge variant={isPublished ? 'default' : 'secondary'}>
              {isPublished ? 'Veröffentlicht' : 'Entwurf'}
            </Badge>
            {isPublished && content.changes_pending ? (
              <Badge variant="outline" className="border-amber-300 text-amber-700">
                Änderungen offen
              </Badge>
            ) : null}
            {dirty && <span className="text-xs text-amber-600">Ungespeicherte Änderungen</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {!hidePreview && (
            <Button type="button" variant="outline" loading={previewing} onClick={handlePreview}>
              <Eye className="mr-2 h-4 w-4" /> Vorschau
            </Button>
          )}
          {darfSpeichern && (
            <Button type="button" variant="secondary" loading={saving} onClick={handleSave} disabled={!dirty}>
              <Save className="mr-2 h-4 w-4" /> Entwurf speichern
            </Button>
          )}
          {darfVeroeffentlichen && (
            <Button type="button" loading={publishing} onClick={handlePublish}>
              <UploadCloud className="mr-2 h-4 w-4" /> Veröffentlichen
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          {isPage ? (
            <CmsBlockEditor
              blockSchemas={blockSchemas}
              blocks={(draft.blocks as Block[]) || []}
              onChange={(blocks) => setDraft({ ...draft, blocks })}
            />
          ) : (
            <SlotEditor
              fields={visibleFields}
              value={draft}
              onChange={setDraft}
              context={parseEditorContext(slug)}
              inherited={inherited}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CmsContentEditor;
